import React, { useState } from 'react';
import { CheckIcon, Share2Icon } from 'lucide-react';
import { ShareCardFrame } from '../engagement/ShareCardFrame';
import { ShareSheet } from '../engagement/ShareSheet';
import { Button } from '../ui/Button';
import { Switch } from '../ui/Switch';
import { communities } from '../../data/communities';
import { engagementConfig } from '../../data/engagement';
import { beeArchetypes } from '../../data/recapArchetypes';
import { useEngagement } from '../../contexts/EngagementContext';
import { useLocale } from '../../contexts/LocaleContext';
import type { BeeArchetypeId, Recap } from '../../types/engagement';
import { hiveSnapshot, percentileEligible, strongestHabits } from '../../utils/recap';
type OptionId = 'streak' | 'challenges' | 'activity' | 'community';

/**
 * Final recap share card. Defaults: period, Bee type, Hive evolution and two positive
 * highlights. Extra items are opt-in. Health data is never available here.
 */
export function RecapShareCard({
  recap,
  archetype,
  communityId
}: {
  recap: Recap;
  archetype: BeeArchetypeId;
  communityId: string;
}) {
  const {
    t,
    tl
  } = useLocale();
  const engagement = useEngagement();
  const [options, setOptions] = useState<Record<OptionId, boolean>>({
    streak: false,
    challenges: false,
    activity: false,
    community: false
  });
  const [sharing, setSharing] = useState(false);
  const [rewardNote, setRewardNote] = useState<'granted' | 'already' | null>(null);
  const start = hiveSnapshot(recap.hiveStartBee);
  const end = hiveSnapshot(recap.hiveEndBee);
  const top = strongestHabits(recap);
  const context = recap.communityContext.find(item => item.communityId === communityId);
  const communityOk = percentileEligible(context);
  const community = communities.find(item => item.id === communityId);
  const reward = engagementConfig.recap.shareRewardBee;
  const alreadyRewarded = engagement.rewardedRecaps.includes(recap.id);
  const toggle = (id: OptionId) => (on: boolean) => setOptions(prev => ({
    ...prev,
    [id]: on
  }));
  const rows: {
    label: string;
    value: string;
  }[] = [{
    label: t('eng.recap.habit'),
    value: top.map(id => t(`habit.${id}.title`)).join(' · ')
  }, {
    label: t('eng.recap.checkDays'),
    value: String(recap.checkDays)
  }];
  if (options.streak) rows.push({
    label: t('eng.recap.streak'),
    value: t('eng.recap.streakValue', {
      n: recap.longestStreak
    })
  });
  if (options.challenges) rows.push({
    label: t('eng.recap.share.opt.challenges'),
    value: String(recap.challengesDone)
  });
  if (options.activity) rows.push({
    label: t('eng.recap.activity'),
    value: tl(recap.mainActivity)
  });
  const handleShared = (target: string) => {
    if (target === 'save') return;
    setRewardNote(engagement.claimRecapShareReward(recap.id) ? 'granted' : 'already');
  };
  const optionList: {
    id: OptionId;
    label: string;
    disabled?: boolean;
  }[] = [{
    id: 'streak',
    label: t('eng.recap.share.opt.streak')
  }, {
    id: 'challenges',
    label: t('eng.recap.share.opt.challenges')
  }, {
    id: 'activity',
    label: t('eng.recap.share.opt.activity')
  }, {
    id: 'community',
    label: t('eng.recap.share.opt.community'),
    disabled: !communityOk
  }];
  return <section aria-label={t('eng.recap.share.overline')} data-id="element-1365" data-mp="86f21ibmn3w9">
      <ShareCardFrame label={t('eng.recap.share.cardLabel')} data-id="element-1366" data-mp="d2cidomw4a91">
        <p className="bee-overline text-[8.5px] font-bold text-ondark" data-id="element-1367" data-mp="h50tk2z2tt96">{tl(recap.periodLabel)}</p>
        <p className="mt-1 text-[22px] font-bold leading-tight text-gold" data-id="element-1368" data-mp="m43kykm4w0yo">{tl(beeArchetypes[archetype].name)}</p>
        <p className="mt-3 text-[10.5px] text-ondark" data-id="element-1369" data-mp="r2wygd1f7ryu">{t('eng.recap.share.hive')}</p>
        <p className="bee-num text-[15px] font-bold text-sand" data-id="element-1370" data-mp="vtww5kid6e2e">
          {t('eng.recap.hive.delta', {
          from: start.cells,
          to: end.cells
        })}
        </p>
        {start.stage.id !== end.stage.id && <p className="text-[11px] text-sand" data-id="element-1371" data-mp="14ulegresj0l3">
            {tl(start.stage.name)} → {tl(end.stage.name)}
          </p>}
        <ul className="mt-3 space-y-1 text-[11.5px]" data-id="element-1372" data-mp="19swob2535b3b">
          {rows.map(row => <li key={row.label} className="flex justify-between gap-2" data-id="element-1373" data-mp="w6foezqxpuk9">
              <span className="text-ondark" data-id="element-1374" data-mp="w4l6luaymike">{row.label}</span>
              <span className="bee-num text-right font-bold" data-id="element-1375" data-mp="s4ui5x8woej1">{row.value}</span>
            </li>)}
        </ul>
        {options.community && communityOk && context && <p className="mt-2 text-[10.5px] leading-snug text-sand" data-id="element-1376" data-mp="10ypc094kuijd">
            {t(recap.period === 'month' ? 'eng.recap.pct.month' : 'eng.recap.pct.year', {
          n: context.topPercent,
          community: community ? tl(community.name) : communityId
        })}
          </p>}
      </ShareCardFrame>

      <div className="mt-3 rounded-card border border-sand-line bg-white/70 px-2" data-id="element-1377" data-mp="n7kjrs3jgsj2">
        <p className="pt-2 text-[12px] font-bold text-deep" data-id="element-1378" data-mp="i6eqnt5yv390">{t('eng.recap.share.options')}</p>
        <ul className="divide-y divide-sand-line" data-id="element-1379" data-mp="1yk4v510zztj6">
          {optionList.map(option => <li key={option.id} className="flex items-center justify-between gap-2 py-2" data-id="element-1380" data-mp="1tla45bsvmynz">
              <span className="min-w-0" data-id="element-1381" data-mp="3bjossrczzkd4">
                <span className={`block text-[13px] ${option.disabled ? 'text-muted' : 'text-deep'}`} data-id="element-1382" data-mp="3bm2on2fi50lq">
                  {option.label}
                </span>
                {option.disabled && <span className="block text-[11px] text-muted" data-id="element-1383" data-mp="32lcvy7ljfqc8">{t('eng.recap.share.opt.communityOff')}</span>}
              </span>
              {option.disabled ? <span className="text-[11px] text-muted" data-id="element-1384" data-mp="3v8h4unnr4ota">—</span> : <Switch checked={options[option.id]} onChange={toggle(option.id)} label={option.label} data-id="element-1385" data-mp="32v4zd4onbw6" />}
            </li>)}
        </ul>
        <p className="pb-2 text-[11px] leading-snug text-muted" data-id="element-1386" data-mp="3qgz3vzlqao8b">{t('eng.recap.share.never')}</p>
      </div>

      <div className="mt-3 rounded-card border border-gold/50 bg-gold/10 p-2" data-id="element-1387" data-mp="i0vwluyj31ki">
        <p className="text-[12.5px] font-bold text-deep" data-id="element-1388" data-mp="ms5ickgyhkr4">{t('eng.recap.reward.title')}</p>
        <p role="status" className="mt-[2px] text-[12px] leading-snug text-deep" data-id="element-1389" data-mp="mtytod2ikt5g">
          {rewardNote === 'granted' ? t('eng.recap.reward.granted', {
          n: reward
        }) : rewardNote === 'already' || alreadyRewarded ? t('eng.recap.reward.already') : t('eng.recap.reward.offer', {
          n: reward
        })}
        </p>
        <p className="mt-1 text-[11px] text-muted" data-id="element-1390" data-mp="hptwf83ji6aw">
          {t('eng.recap.reward.optional')} {t('eng.recap.reward.saveNote')}
        </p>
      </div>

      <Button variant="primary" size="lg" fullWidth className="mt-3" onClick={() => setSharing(true)} data-id="element-1391" data-mp="8yjsqnyt0tsh">
        {alreadyRewarded ? <CheckIcon size={16} aria-hidden="true" data-id="element-1392" data-mp="3yxgagqsvhkq" /> : <Share2Icon size={16} aria-hidden="true" data-id="element-1393" data-mp="3zqdapzyispo" />}
        {t('eng.recap.share.cta')}
      </Button>

      <ShareSheet open={sharing} onClose={() => setSharing(false)} onShared={handleShared} data-id="element-1394" data-mp="3v7l1mwp1j4sa" />
    </section>;
}